import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { api } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";
import { getNextPaginatePage } from "@/utils";

import type {
  Objective,
  ObjectivePeriodStatusSummary,
  ObjectivePeriodSummary,
  ObjectivePeriodUserSummary,
} from "../types";

export type ObjectiveList = Paginate<
  Objective<true, true, true, false, true, true, true>
>;

export const objectives = createQueryKeys("performance-objectives", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  allInfinite: (filter?: SearchRequest) => [{ filter }],
  pending: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number) => [id],

  summary: (search?: SearchRequest) => [{ search }],
  status_summary: (search?: SearchRequest) => [{ search }],
  users: (page?: number, search?: SearchRequest) => [{ page, search }],
});

const include =
  "keyResults,user.position,objectiveType,tags,objectivePeriod,children.keyResults";

export const objectiveDetailInclude =
  "keyResults,user.position,objectiveType,tags,objectivePeriod,parent,children.keyResults,watchers,stakeholders,lastWorkflow";

export const fetchObjectivesQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...objectives.all(page, search),
    queryFn: () =>
      api
        .get<ObjectiveList>(`/performance/objectives`, {
          params: { page, include, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchObjectives = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchObjectivesQueryOptions(page, search));

export const fetchInfiniteObjectivesQueryOptions = (search?: SearchRequest) =>
  infiniteQueryOptions({
    ...objectives.allInfinite(search),
    queryFn: ({ pageParam }) =>
      api
        .get<ObjectiveList>(`/performance/objectives`, {
          params: { page: pageParam, include, ...search },
        })
        .then((response) => response.data),
    initialPageParam: 1,
    getNextPageParam: getNextPaginatePage,
  });

export const useFetchInfiniteObjectives = (search?: SearchRequest) =>
  useInfiniteQuery({ ...fetchInfiniteObjectivesQueryOptions(search) });

export const fetchObjectivesMeQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...objectives.all(page, search),
    queryFn: () =>
      api
        .get<ObjectiveList>(`/performance/objectives/me`, {
          params: { page, include, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchObjectivesMe = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchObjectivesMeQueryOptions(page, search));

export const fetchPendingObjectivesQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...objectives.pending(page, search),
    queryFn: () =>
      api
        .get<ObjectiveList>(`/performance/objectives/pending`, {
          params: { page, include, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchPendingObjectives = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchPendingObjectivesQueryOptions(page, search));

export const fetchObjectiveQueryOptions = (id?: number) =>
  queryOptions({
    ...objectives.one(id),
    queryFn: () =>
      api
        .get<Objective<true, true, true, true, true, true, true>>(
          `/performance/objectives/${id}`,
          {
            params: {
              include: objectiveDetailInclude,
            },
          },
        )
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchObjective = (id?: number) =>
  useQuery(fetchObjectiveQueryOptions(id));

export interface ObjectiveRequest {
  performance_objective_period_id: number;
  title: string;
  weight?: string | null;
  description: string | null;
  performance_objective_type_id: number;
  start_date: string;
  end_date: string;
  user_id: number;
  parent_id?: number | null;
  affects_parent_progress?: boolean;
  visibility: "public" | "private";
  watcher_user_ids?: number[];
  stakeholder_user_ids?: number[];
  key_results: {
    id?: number;
    title: string;
    type: "percentage" | "number" | "money" | "boolean";
    min_value: number;
    max_value: number;
  }[];
  performance_objective_tag_ids: number[];
}

export const useCreateObjective = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: ObjectiveRequest) =>
      api
        .post("/performance/objectives", data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: objectives.all._def,
      });
    },
  });
};

export const useUpdateObjective = (id: number) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: ObjectiveRequest) =>
      api
        .put(`/performance/objectives/${id}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: objectives.all._def,
      });
      queryClient.invalidateQueries({
        queryKey: objectives.allInfinite._def,
      });
      queryClient.invalidateQueries(objectives.one(id));
    },
  });
};

export const useCreateOrUpdateObjective = (id: number | undefined) => {
  const create = useCreateObjective();
  const update = useUpdateObjective(id!);
  return id ? update : create;
};

export const useDeleteObjective = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api
        .delete(`/performance/objectives/${id}`)
        .then((response) => response.data),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: objectives.all._def,
      });
      queryClient.invalidateQueries({
        queryKey: objectives.allInfinite._def,
      });
      queryClient.invalidateQueries(objectives.one(id));
    },
  });
};

/** Invalidates every list and detail query that can show the objective. */
const useInvalidateObjective = () => {
  const queryClient = useQueryClient();
  return (id: number) => {
    queryClient.invalidateQueries({ queryKey: objectives.all._def });
    queryClient.invalidateQueries({ queryKey: objectives.allInfinite._def });
    queryClient.invalidateQueries({ queryKey: objectives.pending._def });
    queryClient.invalidateQueries(objectives.one(id));
  };
};

const useObjectiveActionMutation = (
  action: (id: number) => Promise<unknown>,
) => {
  const invalidate = useInvalidateObjective();
  return useMutationWithError({
    mutationFn: (id: number) => action(id),
    onSuccess: (_, id) => invalidate(id),
  });
};

export const useCancelObjective = () =>
  useObjectiveActionMutation((id) =>
    api
      .patch(`/performance/objectives/${id}/cancel`)
      .then((response) => response.data),
  );

export const useCancelRejectObjective = () => {
  const invalidate = useInvalidateObjective();
  return useMutationWithError({
    mutationFn: ({ id, reason }: { id: number; reason?: string | null }) =>
      api
        .patch(`/performance/objectives/${id}/cancel-reject`, {
          reason: reason || null,
        })
        .then((response) => response.data),
    onSuccess: (_, { id }) => invalidate(id),
  });
};

export const useCancelRequestObjective = () => {
  const invalidate = useInvalidateObjective();
  return useMutationWithError({
    mutationFn: ({ id, reason }: { id: number; reason?: string | null }) =>
      api
        .patch(`/performance/objectives/${id}/cancel-request`, {
          reason: reason || null,
        })
        .then((response) => response.data),
    onSuccess: (_, { id }) => invalidate(id),
  });
};

export const useWithdrawCancelRequestObjective = () =>
  useObjectiveActionMutation((id) =>
    api
      .delete(`/performance/objectives/${id}/cancel-request`)
      .then((response) => response.data),
  );

export const useRestoreRequestObjective = () =>
  useObjectiveActionMutation((id) =>
    api
      .patch(`/performance/objectives/${id}/restore-request`)
      .then((response) => response.data),
  );

export const useRestoreObjective = () =>
  useObjectiveActionMutation((id) =>
    api
      .patch(`/performance/objectives/${id}/restore`)
      .then((response) => response.data),
  );

export interface ObjectiveVersionAction {
  objectiveId: number;
  reason?: string | null;
}

export const useApproveObjectiveVersion = () => {
  const invalidate = useInvalidateObjective();
  return useMutationWithError({
    mutationFn: ({ objectiveId }: ObjectiveVersionAction) =>
      api
        .post(`/performance/objectives/${objectiveId}/versions/approve`)
        .then((response) => response.data),
    onSuccess: (_, { objectiveId }) => invalidate(objectiveId),
  });
};

export const useRejectObjectiveVersion = () => {
  const invalidate = useInvalidateObjective();
  return useMutationWithError({
    mutationFn: ({ objectiveId, reason }: ObjectiveVersionAction) =>
      api
        .post(
          `/performance/objectives/${objectiveId}/versions/reject`,
          reason ? { reason } : undefined,
        )
        .then((response) => response.data),
    onSuccess: (_, { objectiveId }) => invalidate(objectiveId),
  });
};

export interface TagsToObjectiveRequest {
  objectiveId: number;
  data: { performance_objective_tag_ids: number[] };
}

export const useAttachTagsToObjectives = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: ({ objectiveId, data }: TagsToObjectiveRequest) =>
      api
        .post(`performance/objectives/${objectiveId}/tags`, data)
        .then((response) => response.data),
    onSuccess: (_, { objectiveId }) => {
      queryClient.invalidateQueries({
        queryKey: objectives.all._def,
      });
      queryClient.invalidateQueries({
        queryKey: objectives.allInfinite._def,
      });
      queryClient.invalidateQueries(objectives.one(objectiveId));
    },
  });
};

export interface ChangeObjectiveStatusRequest {
  objectiveId: number;
  status: string;
  reason?: string | null;
}

export const useChangeObjectiveStatus = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: ({
      objectiveId,
      status,
      reason,
    }: ChangeObjectiveStatusRequest) =>
      api
        .patch(`/performance/objectives/${objectiveId}/status`, {
          status,
          ...(reason !== undefined ? { reason } : {}),
        })
        .then((response) => response.data),
    onSuccess: (_, { objectiveId, status }) => {
      queryClient.setQueryData(
        objectives.one(objectiveId).queryKey,
        (current: { status?: string } | undefined) =>
          current ? { ...current, status } : current,
      );
      queryClient.invalidateQueries({
        queryKey: objectives.all._def,
      });
      queryClient.invalidateQueries({
        queryKey: objectives.allInfinite._def,
      });
    },
  });
};

// statistics and summaries

export const fetchObjectivePeriodSummaryQueryOptions = (
  search?: SearchRequest,
) =>
  queryOptions({
    ...objectives.summary(search),
    queryFn: () =>
      api
        .get<ObjectivePeriodSummary>(`/performance/objectives/summary`, {
          params: { ...search },
        })
        .then((response) => response.data),
  });

export const useFetchObjectivePeriodSummary = (search?: SearchRequest) =>
  useQuery(fetchObjectivePeriodSummaryQueryOptions(search));

export const fetchObjectivePeriodStatusSummaryQueryOptions = (
  search?: SearchRequest,
) =>
  queryOptions({
    ...objectives.status_summary(search),
    queryFn: () =>
      api
        .get<{
          data: ObjectivePeriodStatusSummary[];
        }>(`/performance/objectives/statuses/summary`, {
          params: { ...search },
        })
        .then((response) => response.data),
  });

export const useFetchObjectivePeriodStatusSummary = (search?: SearchRequest) =>
  useQuery(fetchObjectivePeriodStatusSummaryQueryOptions(search));

export const fetchObjectivePeriodUsersSummaryQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...objectives.users(page, search),
    queryFn: () =>
      api
        .get<
          Paginate<ObjectivePeriodUserSummary<true>>
        >(`/performance/objectives/users/summary`, { params: { page, ...search } })
        .then((response) => response.data),
  });

export const useFetchObjectivePeriodUsersSummary = (search?: SearchRequest) =>
  usePaginateQuery((page) =>
    fetchObjectivePeriodUsersSummaryQueryOptions(page, search),
  );
